import {
  Activity,
  ArrowDownToLine,
  Check,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  LoaderCircle,
  Minus,
  PanelRightClose,
  TriangleAlert,
  X,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { WorkspaceFilesPanel } from "./WorkspaceFilesPanel";
import type { FileVersion } from "@rnavoyager/contracts";
import { usePlanFollowScroll } from "./usePlanFollowScroll";
import { isPlanSnapshotEvent, publicPlanFromEvent } from "./plan-events";
import { planRevisionMarkers } from "./plan-changes";
import { TaskPlanChanges } from "./TaskPlanChanges";
import type { PublicPlan, TaskEvent, TaskRuntimeState } from "./types";

import { TaskExecutionSteps, WorkspacePlanHeading } from "./TaskExecutionSteps";
import type { PlanStepStatus } from "./TaskExecutionSteps";
export type { PlanStepStatus } from "./TaskExecutionSteps";

export type ExecutionActivityStatus = "running" | "succeeded" | "failed" | "cancelled" | "unknown";

export interface ExecutionActivity {
  id: string;
  actionIds: string[];
  firstCursor: number;
  lastCursor: number;
  label: string;
  status: ExecutionActivityStatus;
  statusLabel: string;
  detail: string;
  startedAt: string;
  finishedAt: string | null;
  durationSeconds: number | null;
  jobId: string | null;
  progressUpdatedAt: string | null;
  progressDetail: string;
}

export interface ExecutionStatusSummary {
  label: string;
  active: boolean;
  tone: "running" | "pending" | "success" | "failed" | "muted";
}

type FileInputIssue = NonNullable<
  NonNullable<TaskEvent["file_input_rejection"]>["fileInputs"]
>[number];

interface FileRejectionNotice {
  cursor: number;
  functionName: string;
  issue: FileInputIssue;
}

type ExecutionStreamItem =
  | { kind: "activity"; cursor: number; activity: ExecutionActivity }
  | { kind: "rejection"; cursor: number; notice: FileRejectionNotice };

const LEGACY_SESSION_ACTIVITY_LABEL = "会话内操作（详情缺失）";
const SESSION_OPERATION_LABELS: Record<string, string> = {
  write_text: "写入文本",
  workspace_list: "列出目录",
  workspace_stat: "检查文件状态",
  workspace_read: "读取文件",
  workspace_search: "搜索工作区",
  apply_patch: "修改文件",
  run_command: "执行命令",
  start_command: "启动后台命令",
  command_status: "检查后台作业",
  cancel_command: "取消后台作业",
  command_logs: "读取后台日志",
  workspace_mkdir: "创建目录",
  workspace_copy: "复制文件",
  workspace_move: "移动文件",
  workspace_remove: "删除文件",
  workspace_restore: "恢复文件",
  inspect_structured_file: "检查结构化文件",
  install_dependencies: "安装依赖",
  git_init: "初始化 Git",
  git_status: "检查 Git 状态",
  git_diff: "查看 Git 差异",
  git_log: "查看 Git 历史",
  git_commit: "提交 Git 变更",
  git_restore: "恢复 Git 文件",
  materialize_mcp_artifact: "保存工具产物",
  write_report: "写入报告",
};

const PLAN_OBJECTIVE_MAX_CHARACTERS = 50;

export function truncatePlanObjective(objective: string): string {
  const characters = Array.from(objective.trim());
  if (characters.length <= PLAN_OBJECTIVE_MAX_CHARACTERS) return characters.join("");
  return `${characters.slice(0, PLAN_OBJECTIVE_MAX_CHARACTERS - 1).join("")}…`;
}

interface PlanHistoryEntry {
  key: string;
  plan: PublicPlan | null;
  startCursor: number;
  endCursor: number;
  current: boolean;
}

export function WorkspaceRightRail({
  sessionId,
  runtime,
  workspaceRevision = 0,
  onClose,
  fileRequest,
  onFileRequestHandled,
  onReferenceFile,
  referenceDisabledReason,
}: {
  sessionId: string;
  runtime: TaskRuntimeState;
  workspaceRevision?: number;
  onClose: () => void;
  fileRequest?: { path: string } | null;
  onFileRequestHandled?: () => void;
  onReferenceFile?: (file: FileVersion) => void;
  referenceDisabledReason?: string;
}) {
  const planHistory = useMemo(() => buildPlanHistory(runtime), [runtime]);
  const historySignature = planHistory.map((entry) => entry.key).join("|");
  const [selectedPlanKey, setSelectedPlanKey] = useState("");
  const requestedPlanIndex = planHistory.findIndex((entry) => entry.key === selectedPlanKey);
  const selectedPlanIndex =
    requestedPlanIndex >= 0 ? requestedPlanIndex : Math.max(0, planHistory.length - 1);
  const selectedEntry = planHistory[selectedPlanIndex] ?? planHistory.at(-1) ?? null;
  const viewingCurrent = selectedEntry?.current ?? true;
  const plan = selectedEntry?.plan ?? null;
  const displayedTurnId =
    viewingCurrent && runtime.active_turn_id
      ? runtime.active_turn_id
      : ([...runtime.events]
          .reverse()
          .find((event) => publicPlanFromEvent(event)?.plan_hash === plan?.plan_hash)?.turn_id ??
        null);
  const revisionMarkers = useMemo(
    () => planRevisionMarkers(runtime.events, displayedTurnId),
    [runtime.events, displayedTurnId],
  );
  const [changeView, setChangeView] = useState({ sessionId: "", turnId: "", open: false });
  const changesOpen =
    changeView.sessionId === sessionId && changeView.turnId === displayedTurnId && changeView.open;
  const displayedObjective = plan
    ? truncatePlanObjective(plan.objective)
    : planPlaceholderTitle(runtime.state);
  const planEvents = useMemo(
    () =>
      selectedEntry
        ? runtime.events.filter(
            (event) =>
              event.cursor >= selectedEntry.startCursor && event.cursor < selectedEntry.endCursor,
          )
        : runtime.events,
    [runtime.events, selectedEntry],
  );
  const planState = viewingCurrent ? runtime.state : historicalPlanState(planEvents);
  const statuses = useMemo(
    () => resolvePlanStepStatuses(plan, planState, planEvents),
    [plan, planEvents, planState],
  );
  const focusIndex = useMemo(() => resolvePlanFocusIndex(statuses), [statuses]);
  const executionScrollRef = useRef<HTMLDivElement>(null);
  const focusStepRef = useRef<HTMLLIElement>(null);
  const { following, hasMore, resumeFollowing } = usePlanFollowScroll({
    containerRef: executionScrollRef,
    stepRef: focusStepRef,
    focusKey: `${sessionId}:${selectedEntry?.key ?? "none"}:${String(focusIndex)}`,
    enabled: !changesOpen && viewingCurrent && plan !== null && focusIndex >= 0,
    resetKey: `${sessionId}:${historySignature}:${String(viewingCurrent)}:${String(changesOpen)}`,
  });
  const activities = useMemo(
    () => settleExecutionActivities(resolveExecutionActivities(planEvents), planState),
    [planEvents, planState],
  );
  const firstPlanCursor = planHistory[0]?.startCursor ?? Number.POSITIVE_INFINITY;
  const prePlanActivities = useMemo(
    () =>
      plan && selectedPlanIndex === 0
        ? settleExecutionActivities(
            resolveExecutionActivities(
              runtime.events.filter((event) => event.cursor < firstPlanCursor),
            ),
            planState,
          )
        : [],
    [firstPlanCursor, plan, planState, runtime.events, selectedPlanIndex],
  );
  const displayedActivities = useMemo(
    () =>
      [...prePlanActivities, ...activities].sort(
        (left, right) => left.firstCursor - right.firstCursor,
      ),
    [activities, prePlanActivities],
  );
  const fileRejections = useMemo(() => {
    const notices: FileRejectionNotice[] = [];
    for (const event of planEvents) {
      const rejection = event.file_input_rejection;
      if (!rejection) continue;
      for (const issue of rejection.fileInputs ?? []) {
        notices.push({
          cursor: event.cursor,
          functionName: rejection.functionName ?? "消费者",
          issue,
        });
      }
    }
    return notices.sort((left, right) => left.cursor - right.cursor);
  }, [planEvents]);
  const preparationBadges = useMemo(() => {
    const badges = new Map<string, string>();
    for (const event of planEvents) {
      const outcome = event.file_preparation;
      if (!outcome) continue;
      const label = outcome.kind === "inspect" ? "格式检查" : "格式转换";
      badges.set(outcome.actionId, outcome.status === "succeeded" ? `${label} ✓` : `${label}失败`);
    }
    return badges;
  }, [planEvents]);
  const streamItems = useMemo<ExecutionStreamItem[]>(
    () =>
      [
        ...displayedActivities.map((activity): ExecutionStreamItem => ({
          kind: "activity",
          cursor: activity.firstCursor,
          activity,
        })),
        ...fileRejections.map((notice): ExecutionStreamItem => ({
          kind: "rejection",
          cursor: notice.cursor,
          notice,
        })),
      ].sort((left, right) => left.cursor - right.cursor),
    [displayedActivities, fileRejections],
  );
  const hasActiveStep = statuses.includes("active");
  const hasRunningActivity = displayedActivities.some((activity) => activity.status === "running");
  const currentTime = useCurrentTime(hasActiveStep || hasRunningActivity);
  const actionStreamId = useId();
  const [activitiesOpen, setActivitiesOpen] = useState(true);
  const [filePanel, setFilePanel] = useState({ sessionId, expanded: true });
  const filesExpanded = filePanel.sessionId !== sessionId || filePanel.expanded;
  const workspacePreparing =
    runtime.state === "preparing" ||
    runtime.session_status === "provisioning" ||
    runtime.session_status === "rotating";

  useEffect(() => {
    if (fileRequest) setFilePanel({ sessionId, expanded: true });
  }, [fileRequest, sessionId]);

  useEffect(() => {
    setActivitiesOpen(true);
  }, [sessionId]);

  useEffect(() => {
    setSelectedPlanKey(planHistory.at(-1)?.key ?? "");
  }, [historySignature]); // 新规划出现时自动翻到最新一份。

  return (
    <>
      <aside
        aria-label="任务执行与文件树"
        className={`workspace-right-rail workspace-rail${filesExpanded ? "" : " files-collapsed"}`}
      >
        <section className="workspace-rail-card workspace-plan-panel">
          <WorkspacePlanHeading
            title={displayedObjective}
            objective={plan?.objective}
            label={viewingCurrent ? "任务执行" : "历史任务执行"}
            actions={
              <>
                <button
                  aria-controls="task-plan-changes-content"
                  aria-expanded={changesOpen}
                  aria-label="任务改动"
                  className={`workspace-plan-changes-toggle${changesOpen ? " active" : ""}`}
                  disabled={!displayedTurnId || revisionMarkers.length === 0}
                  onClick={() =>
                    setChangeView({ sessionId, turnId: displayedTurnId!, open: !changesOpen })
                  }
                  title={changesOpen ? "返回当前执行步骤" : "查看任务规划的历次改动"}
                  type="button"
                >
                  <GitBranch aria-hidden="true" size={15} />
                </button>
                <button
                  aria-label="收起右侧面板"
                  className="workspace-rail-icon-button workspace-rail-close"
                  onClick={onClose}
                  title="收起右侧面板"
                >
                  <PanelRightClose size={15} />
                </button>
              </>
            }
          />

          {planHistory.length > 1 && (
            <nav aria-label="切换任务规划" className="workspace-plan-pager">
              <button
                aria-label="查看上一份任务规划"
                disabled={selectedPlanIndex === 0}
                onClick={() => setSelectedPlanKey(planHistory[selectedPlanIndex - 1].key)}
              >
                <ChevronLeft size={14} />
              </button>
              <span>
                {selectedPlanIndex + 1} / {planHistory.length}
                <small>{selectedEntry?.current ? "当前" : "历史"}</small>
              </span>
              <button
                aria-label="查看下一份任务规划"
                disabled={selectedPlanIndex === planHistory.length - 1}
                onClick={() => setSelectedPlanKey(planHistory[selectedPlanIndex + 1].key)}
              >
                <ChevronRight size={14} />
              </button>
            </nav>
          )}

          <div
            className={`workspace-execution-scroll${following ? "" : " follow-paused"}`}
            ref={executionScrollRef}
          >
            {changesOpen && displayedTurnId ? (
              <div id="task-plan-changes-content">
                <TaskPlanChanges
                  key={`${sessionId}:${displayedTurnId}`}
                  sessionId={sessionId}
                  turnId={displayedTurnId}
                  markers={revisionMarkers}
                />
              </div>
            ) : (
              <>
                {plan && (
                  <TaskExecutionSteps
                    steps={plan.operations.map((operation) => ({
                      id: operation.operation_id,
                      description: operation.description,
                      acceptanceCriteria: operation.acceptance_criteria,
                    }))}
                    statuses={statuses}
                    focusIndex={focusIndex}
                    focusRef={focusStepRef}
                  />
                )}

                {plan || streamItems.length > 0 ? (
                  <section
                    aria-label={plan ? "执行活动" : "即时执行活动"}
                    className="workspace-action-stream"
                  >
                    <button
                      aria-controls={actionStreamId}
                      aria-expanded={activitiesOpen}
                      className="workspace-action-heading"
                      onClick={() => setActivitiesOpen((open) => !open)}
                      type="button"
                    >
                      <Activity size={14} />
                      <strong>执行活动</strong>
                      <span>{streamItems.length}</span>
                      <ChevronRight
                        aria-hidden="true"
                        className={activitiesOpen ? "expanded" : ""}
                        size={14}
                      />
                    </button>
                    <div id={actionStreamId}>
                      {activitiesOpen &&
                        (streamItems.length === 0 ? (
                          <p className="workspace-action-empty">尚未执行受控动作。</p>
                        ) : (
                          <ol className="workspace-activity-list workspace-activity-stream">
                            {streamItems.map((item) =>
                              item.kind === "activity" ? (
                                <ExecutionActivityRow
                                  activity={item.activity}
                                  currentTime={currentTime}
                                  key={item.activity.id}
                                  preparationBadges={preparationBadges}
                                />
                              ) : (
                                <FileRejectionRow
                                  key={`rejection:${item.cursor}:${String(item.notice.issue.index)}`}
                                  notice={item.notice}
                                />
                              ),
                            )}
                          </ol>
                        ))}
                    </div>
                  </section>
                ) : (
                  <div aria-live="polite" className="workspace-plan-empty">
                    {runtime.state === "planning" ? (
                      <LoaderCircle className="spin" size={20} />
                    ) : (
                      <GitBranch size={20} />
                    )}
                    <span>{planPlaceholderDetail(runtime.state)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {!changesOpen && hasMore && <span aria-hidden="true" className="workspace-scroll-fade" />}

          {!changesOpen && !following && viewingCurrent && focusIndex >= 0 && (
            <button
              className="workspace-plan-follow-chip"
              onClick={resumeFollowing}
              title="滚动到正在执行的步骤"
              type="button"
            >
              <ArrowDownToLine aria-hidden="true" size={13} />
              回到当前任务
            </button>
          )}
        </section>

        <WorkspaceFilesPanel
          sessionId={sessionId}
          refreshKey={`${runtime.events_cursor}:${workspaceRevision}`}
          expanded={filesExpanded}
          onToggle={() => setFilePanel({ sessionId, expanded: !filesExpanded })}
          ready={!workspacePreparing}
          events={runtime.events}
          fileRequest={fileRequest}
          onFileRequestHandled={onFileRequestHandled}
          onReferenceFile={onReferenceFile}
          referenceDisabledReason={referenceDisabledReason}
        />
      </aside>
    </>
  );
}

function buildPlanHistory(runtime: TaskRuntimeState): PlanHistoryEntry[] {
  const entries: PlanHistoryEntry[] = [];
  const seen = new Set<string>();
  const events = [...runtime.events].sort((left, right) => left.cursor - right.cursor);

  for (const event of events) {
    if (!isPlanSnapshotEvent(event)) continue;
    const plan = publicPlanFromEvent(event);
    if (!plan) continue;
    const key = planHistoryKey(plan);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({
      key,
      plan,
      startCursor: event.cursor,
      endCursor: Number.POSITIVE_INFINITY,
      current: runtime.plan?.plan_hash === plan.plan_hash,
    });
  }

  if (runtime.plan) {
    const key = planHistoryKey(runtime.plan);
    const existing = entries.find((entry) => entry.key === key);
    if (existing) {
      existing.plan = runtime.plan;
      existing.current = true;
    } else {
      entries.push({
        key,
        plan: runtime.plan,
        startCursor: entries.length > 0 ? (events.at(-1)?.cursor ?? 0) : 0,
        endCursor: Number.POSITIVE_INFINITY,
        current: true,
      });
    }
  } else if (runtime.state === "planning") {
    entries.push({
      key: `pending:${runtime.active_turn_id ?? "current"}`,
      plan: null,
      startCursor: events.at(-1)?.cursor ?? 0,
      endCursor: Number.POSITIVE_INFINITY,
      current: true,
    });
  }

  entries.sort((left, right) => left.startCursor - right.startCursor);
  entries.forEach((entry, index) => {
    entry.endCursor = entries[index + 1]?.startCursor ?? Number.POSITIVE_INFINITY;
  });
  return entries;
}

function planHistoryKey(plan: PublicPlan): string {
  return `${plan.execution_plan_id}:${plan.plan_hash}:${plan.revision}`;
}

function historicalPlanState(events: TaskEvent[]): TaskRuntimeState["state"] {
  if (
    events.some(
      (event) =>
        event.event_type === "execution_completed" || event.event_type === "agentic_loop_completed",
    )
  )
    return "completed";
  if (
    events.some(
      (event) => event.event_type === "task_failed" || event.event_type === "task_stop_failed",
    )
  )
    return "failed";
  // 该函数只处理已被后续规划替代的历史条目，其未完成步骤均不会再执行。
  return "cancelled";
}

export function resolvePlanStepStatuses(
  plan: PublicPlan | null,
  runtimeState: TaskRuntimeState["state"],
  events: TaskEvent[],
): PlanStepStatus[] {
  const count = plan?.operations.length ?? 0;
  if (!plan || count === 0) return [];
  if (runtimeState === "completed") {
    return Array.from({ length: count }, () => "success");
  }
  const relevantEvents = eventsForPlan(plan, events);
  const taskFailed =
    runtimeState === "failed" ||
    runtimeState === "stop_failed" ||
    relevantEvents.some(
      (event) => event.event_type === "task_failed" || event.event_type === "task_stop_failed",
    );
  const taskCancelled =
    runtimeState === "cancelled" ||
    relevantEvents.some(
      (event) =>
        event.event_type === "task_cancelled" ||
        event.event_type === "task_stopped" ||
        event.event_type === "plan_discarded",
    );

  const hasSnapshotStatuses = plan.operations.every((operation) => operation.status !== undefined);
  return plan.operations.map((operation, index) => {
    if (operation.status === "completed") return "success";
    if (operation.status === "failed") return "failed";
    if (operation.status === "cancelled") return "cancelled";
    // Turn 可能在下一次计划修订前终止；收敛未结束步骤，保留快照中的已有终态。
    if (taskFailed) return operation.status === "in_progress" ? "failed" : "blocked";
    if (taskCancelled) return "cancelled";
    if (operation.status === "in_progress") return "active";
    if (
      !hasSnapshotStatuses &&
      index === 0 &&
      (runtimeState === "executing" || runtimeState === "running")
    )
      return "active";
    return "pending";
  });
}

/**
 * 选择跟随滚动的焦点步骤：执行中优先，其次失败断点，再次已停止步骤，
 * 全部完成时退化为最后一步；没有可聚焦结果时返回 -1。
 */
export function resolvePlanFocusIndex(statuses: PlanStepStatus[]): number {
  const active = statuses.indexOf("active");
  if (active >= 0) return active;
  const failed = statuses.indexOf("failed");
  if (failed >= 0) return failed;
  const stopped = statuses.findIndex((status) => status === "cancelled" || status === "blocked");
  if (stopped >= 0) return stopped;
  return statuses.lastIndexOf("success");
}

export function resolveExecutionActivities(events: TaskEvent[]): ExecutionActivity[] {
  const actions = new Map<string, ExecutionActivity>();
  const orderedEvents = [...events].sort((left, right) => left.cursor - right.cursor);
  const latestProgress = new Map<string, { detail: string; updatedAt: string }>();
  const jobKeys = new Map<string, string>();
  const liveJobs = new Set<string>();

  for (const event of orderedEvents) {
    if (
      event.event_type !== "action_granted" &&
      event.event_type !== "action_started" &&
      event.event_type !== "action_progress" &&
      event.event_type !== "action_completed" &&
      event.event_type !== "observation_recorded"
    ) {
      continue;
    }
    const actionId = stringPayload(event, "action_id");
    if (!actionId) continue;
    const current = actions.get(actionId) ?? emptyExecutionActivity(event, actionId);
    // 迟到快照不能重开已完成的动作。
    if (event.event_type === "action_progress" && current.finishedAt) continue;
    current.lastCursor = event.cursor;

    if (isActionStartEvent(event)) {
      current.label = executionActivityLabel(event, current.label);
      current.detail = executionActivityPublicDetail(event) || current.detail;
      current.status = "running";
      current.statusLabel = "正在执行";
      if (parseTimestamp(event.timestamp) < parseTimestamp(current.startedAt)) {
        current.startedAt = event.timestamp;
      }
      actions.set(actionId, current);
      continue;
    }

    const jobProgress = recordPayload(event, "job_progress");
    if (jobProgress) {
      const jobId = boundedString(jobProgress.job_id);
      const tool = recordPayload(event, "tool");
      const server = boundedString(tool?.server) ?? "";
      const toolName = boundedString(tool?.tool) ?? boundedString(jobProgress.tool) ?? "";
      const observedAt = boundedString(jobProgress.observed_at) ?? event.timestamp;
      if (jobId) {
        const key = JSON.stringify([server, toolName, jobId]);
        jobKeys.set(actionId, key);
        if (event.event_type === "action_progress") liveJobs.add(key);
        const previous = latestProgress.get(key);
        if (!previous || parseTimestamp(observedAt) > parseTimestamp(previous.updatedAt)) {
          latestProgress.set(key, {
            detail: activityDetail(jobProgress, null),
            updatedAt: observedAt,
          });
        }
      }
    }
    if (event.event_type === "action_progress") {
      const observedAt = boundedString(jobProgress?.observed_at);
      if (
        !observedAt ||
        (current.progressUpdatedAt &&
          parseTimestamp(observedAt) <= parseTimestamp(current.progressUpdatedAt))
      )
        continue;
      current.label = executionActivityLabel(event, current.label);
      current.jobId = boundedString(jobProgress?.job_id);
      current.progressUpdatedAt = observedAt;
      current.progressDetail = activityDetail(jobProgress, null);
      actions.set(actionId, current);
      continue;
    }
    const actionFailure = recordPayload(event, "action_failure");
    current.label = executionActivityLabel(event, current.label);
    current.durationSeconds = numberPayload(event, "duration_seconds");
    const explicitStartedAt = stringPayload(event, "started_at");
    if (
      explicitStartedAt &&
      parseTimestamp(explicitStartedAt) < parseTimestamp(current.startedAt)
    ) {
      current.startedAt = explicitStartedAt;
    }
    const jobId = boundedString(jobProgress?.job_id);
    if (jobId) current.jobId = jobId;
    // 动作终态与远端异步作业状态是两个生命周期。工具返回 queued/running 只进入详情，
    // 不能把已经闭合的 Action/Observation 重新解释为仍在执行。
    const status = activityStatus(
      stringPayload(event, "status") ??
        (event.event_type === "action_completed" ? "succeeded" : null),
    );
    current.status = status.status;
    current.statusLabel = status.label;
    current.detail = joinActivityDetails(
      current.detail,
      executionActivityPublicDetail(event),
      activityDetail(null, actionFailure),
      executionActivityResultDetail(event),
    );
    current.progressDetail = activityDetail(jobProgress, null);
    current.finishedAt = status.status === "running" ? null : event.timestamp;
    actions.set(actionId, current);
  }

  // 相同远端作业跨 submit/get/wait 的旧快照一起更新，但各动作仍按自己的终态计时。
  for (const [actionId, activity] of actions) {
    const key = jobKeys.get(actionId);
    const progress = key ? latestProgress.get(key) : undefined;
    if (progress) {
      activity.progressDetail = progress.detail;
      if (activity.progressUpdatedAt || (key && liveJobs.has(key))) {
        activity.progressUpdatedAt = progress.updatedAt;
      }
    }
    activity.detail = joinActivityDetails(activity.detail, activity.progressDetail);
  }
  const collapsed = new Map<string, ExecutionActivity>();
  for (const activity of [...actions.values()].sort(
    (left, right) => left.firstCursor - right.firstCursor,
  )) {
    const key = activity.jobId
      ? `job:${jobKeys.get(activity.actionIds[0]!) ?? activity.jobId}:${activity.label}`
      : activity.id;
    const existing = collapsed.get(key);
    if (!existing) {
      collapsed.set(key, { ...activity, id: key });
      continue;
    }
    const startedAt =
      parseTimestamp(activity.startedAt) < parseTimestamp(existing.startedAt)
        ? activity.startedAt
        : existing.startedAt;
    collapsed.set(key, {
      ...existing,
      id: key,
      actionIds: [...new Set([...existing.actionIds, ...activity.actionIds])],
      firstCursor: Math.min(existing.firstCursor, activity.firstCursor),
      lastCursor: Math.max(existing.lastCursor, activity.lastCursor),
      label: activity.label,
      status: activity.status,
      statusLabel: activity.statusLabel,
      detail: activity.detail || existing.detail,
      startedAt,
      finishedAt: activity.finishedAt,
      // 同一远端作业的轮询调用不应把各次请求耗时相加成作业耗时。
      durationSeconds: null,
      jobId: activity.jobId,
      progressUpdatedAt: activity.progressUpdatedAt,
      progressDetail: activity.progressDetail,
    });
  }
  return [...collapsed.values()]
    .sort((left, right) => left.firstCursor - right.firstCursor)
    .map((activity) =>
      activity.label === LEGACY_SESSION_ACTIVITY_LABEL
        ? {
            ...activity,
            detail: activity.detail || "该历史事件未记录安全动作摘要，无法恢复具体操作。",
          }
        : activity,
    );
}

function settleExecutionActivities(
  activities: ExecutionActivity[],
  runtimeState: TaskRuntimeState["state"],
): ExecutionActivity[] {
  if (
    runtimeState !== "completed" &&
    runtimeState !== "failed" &&
    runtimeState !== "stop_failed" &&
    runtimeState !== "cancelled"
  ) {
    return activities;
  }
  return activities.map((activity) => {
    if (activity.status !== "running") return activity;
    if (runtimeState === "completed") {
      return { ...activity, status: "succeeded", statusLabel: "执行完成" };
    }
    if (runtimeState === "cancelled") {
      return { ...activity, status: "cancelled", statusLabel: "已停止" };
    }
    return { ...activity, status: "failed", statusLabel: "随任务终止" };
  });
}

export function executionStatusSummary(runtime: TaskRuntimeState): ExecutionStatusSummary {
  if (
    runtime.execution_phase === "report" &&
    runtime.state === "completed" &&
    runtime.result_document?.report?.state === "unapproved"
  )
    return { label: "研究已完成 · 报告交付失败", active: false, tone: "failed" };
  if (
    runtime.execution_phase === "report" &&
    ["planning", "executing", "running", "queued"].includes(runtime.state)
  )
    return { label: "研究已完成 · 正在交付报告", active: true, tone: "running" };
  if (runtime.state === "planning") {
    return { label: "正在生成任务规划", active: true, tone: "running" };
  }
  if (runtime.state === "awaiting_plan_start") {
    return { label: "任务规划已就绪，等待开始执行", active: false, tone: "pending" };
  }
  if (runtime.state === "awaiting_action_approval") {
    return { label: "等待确认动作", active: false, tone: "pending" };
  }
  if (runtime.state === "awaiting_user_input") {
    return { label: "等待补充任务信息", active: false, tone: "pending" };
  }
  if (runtime.state === "stopping") {
    return { label: "正在停止任务", active: true, tone: "running" };
  }
  if (runtime.state === "failed" || runtime.state === "stop_failed") {
    return { label: "任务执行失败", active: false, tone: "failed" };
  }
  if (runtime.state === "completed") {
    return { label: "任务执行完成", active: false, tone: "success" };
  }
  if (runtime.state === "cancelled") {
    return { label: "任务已停止", active: false, tone: "muted" };
  }

  const activities = resolveExecutionActivities(runtime.events);
  const runningActivity = [...activities]
    .reverse()
    .find((activity) => activity.status === "running");
  if (runtime.plan) {
    const statuses = resolvePlanStepStatuses(runtime.plan, runtime.state, runtime.events);
    const activeIndex = statuses.findIndex((status) => status === "active");
    if (activeIndex >= 0) {
      const operation = runtime.plan.operations[activeIndex];
      return {
        label: `${activeIndex + 1}/${runtime.plan.operations.length} · ${operation.description}`,
        active: true,
        tone: "running",
      };
    }
  }
  if (runningActivity) {
    return { label: `即时执行 · ${runningActivity.label}`, active: true, tone: "running" };
  }
  if (runtime.state === "executing" || runtime.state === "running") {
    return { label: "即时执行 · 等待下一个受控动作", active: true, tone: "running" };
  }
  return { label: "尚未开始执行", active: false, tone: "muted" };
}

function emptyExecutionActivity(event: TaskEvent, actionId: string): ExecutionActivity {
  return {
    id: `action:${actionId}`,
    actionIds: [actionId],
    firstCursor: event.cursor,
    lastCursor: event.cursor,
    label: executionActivityLabel(event, "受控动作"),
    status: "running",
    statusLabel: "正在执行",
    detail: "",
    startedAt: event.timestamp,
    finishedAt: null,
    durationSeconds: null,
    jobId: null,
    progressUpdatedAt: null,
    progressDetail: "",
  };
}

function executionActivityLabel(event: TaskEvent, fallback: string): string {
  const tool = recordPayload(event, "tool");
  const progress = recordPayload(event, "job_progress");
  const failure = recordPayload(event, "action_failure");
  const publicDetails = recordPayload(event, "public_details");
  const publicResult = recordPayload(event, "public_result");
  const disclosure = recordPayload(event, "data_disclosure");
  const capturedParameters = recordPayload(event, "parameters");
  const toolName = boundedString(tool?.tool) ?? boundedString(progress?.tool);
  const functionName = boundedString(tool?.function) ?? boundedString(progress?.function);
  // MCP 函数名必须原样展示，不能用点号改写命名空间分隔符。
  if (functionName) return functionName;
  if (toolName) return toolName;
  const operation =
    boundedString(publicDetails?.operation) ??
    boundedString(publicResult?.operation) ??
    boundedString(disclosure?.operation) ??
    boundedString(capturedParameters?.operation);
  if (operation) return sessionOperationLabel(operation);
  const failedAction = boundedString(failure?.action);
  if (failedAction) return sessionOperationLabel(failedAction);
  if (fallback !== "受控动作") return fallback;
  const kind = stringPayload(event, "kind");
  const labels: Record<string, string> = {
    tool: "生信工具调用",
    session_code: LEGACY_SESSION_ACTIVITY_LABEL,
    report: "生成报告",
    web_search: "网页搜索",
  };
  return kind ? (labels[kind] ?? kind) : fallback;
}

function sessionOperationLabel(operation: string): string {
  const label = SESSION_OPERATION_LABELS[operation];
  return label ? `${label} · ${operation}` : operation;
}

function executionActivityPublicDetail(event: TaskEvent): string {
  const details =
    recordPayload(event, "public_details") ??
    recordPayload(event, "data_disclosure") ??
    recordPayload(event, "parameters");
  if (!details) return "";
  const operation = boundedString(details.operation);
  const parts: string[] = [];
  const query = boundedString(details.query);
  if (query)
    parts.push(operation === "workspace_search" ? `搜索内容：${query}` : `搜索查询：${query}`);
  const argv = stringArray(details.argv);
  if (argv.length > 0) parts.push(`命令：${argv.map(formatCommandArgument).join(" ")}`);
  const path = boundedString(details.path);
  if (path) parts.push(`路径：${path}`);
  const sourcePath = boundedString(details.source_path);
  const destinationPath = boundedString(details.destination_path);
  if (sourcePath) parts.push(`源：${sourcePath}`);
  if (destinationPath) parts.push(`目标：${destinationPath}`);
  const cwd = boundedString(details.cwd);
  if (cwd) parts.push(`工作目录：${cwd}`);
  const paths = stringArray(details.paths);
  if (paths.length > 0) parts.push(`路径：${paths.join("、")}`);
  const outputPaths = stringArray(details.output_paths);
  if (outputPaths.length > 0) parts.push(`预期输出：${outputPaths.join("、")}`);
  const jobId = boundedString(details.job_id);
  if (jobId) parts.push(`作业：${jobId}`);
  const manager = boundedString(details.manager);
  const manifestPath = boundedString(details.manifest_path);
  if (manager) parts.push(`依赖管理器：${manager}`);
  if (manifestPath) parts.push(`清单：${manifestPath}`);
  const message = boundedString(details.message);
  if (message) parts.push(`提交说明：${message}`);
  const contentBytes = boundedNonNegativeNumber(details.content_bytes);
  if (contentBytes !== null) parts.push(`写入：${formatByteCount(contentBytes)}`);
  const patchLines = boundedNonNegativeNumber(details.patch_lines);
  if (patchLines !== null) parts.push(`补丁：${patchLines} 行`);
  const parameterSummaries = stringArray(details.parameter_summaries);
  if (parameterSummaries.length > 0) parts.push(`参数：${parameterSummaries.join("、")}`);
  return parts.join(" · ");
}

function executionActivityResultDetail(event: TaskEvent): string {
  const result = recordPayload(event, "public_result") ?? recordPayload(event, "data_disclosure");
  if (!result) return "";
  const parts: string[] = [];
  const searchCallCount = boundedNonNegativeNumber(result.search_call_count);
  const resultCount = boundedNonNegativeNumber(result.result_count);
  const citationCount = boundedNonNegativeNumber(result.citation_count);
  if (searchCallCount !== null) parts.push(`搜索调用 ${searchCallCount} 次`);
  if (resultCount !== null) parts.push(`返回 ${resultCount} 条结果`);
  if (citationCount !== null) parts.push(`引用 ${citationCount} 条`);
  const returncode = finiteNumber(result.returncode);
  if (returncode !== null) parts.push(`退出码 ${returncode}`);
  const entryCount = boundedNonNegativeNumber(result.entry_count);
  const matchCount = boundedNonNegativeNumber(result.match_count);
  const artifactCount = boundedNonNegativeNumber(result.artifact_count);
  if (entryCount !== null) parts.push(`发现 ${entryCount} 项`);
  if (matchCount !== null) parts.push(`匹配 ${matchCount} 处`);
  if (artifactCount !== null) parts.push(`产出 ${artifactCount} 个文件`);
  const returnedBytes = boundedNonNegativeNumber(result.returned_bytes);
  const scannedFiles = boundedNonNegativeNumber(result.scanned_files);
  const scannedBytes = boundedNonNegativeNumber(result.scanned_bytes);
  if (returnedBytes !== null) parts.push(`读取 ${formatByteCount(returnedBytes)}`);
  if (scannedFiles !== null) parts.push(`扫描 ${scannedFiles} 个文件`);
  if (scannedBytes !== null) parts.push(`扫描 ${formatByteCount(scannedBytes)}`);
  const additions = boundedNonNegativeNumber(result.additions);
  const deletions = boundedNonNegativeNumber(result.deletions);
  if (additions !== null || deletions !== null) {
    parts.push(`变更 +${additions ?? 0}/-${deletions ?? 0} 行`);
  }
  const size = boundedNonNegativeNumber(result.size);
  if (size !== null) parts.push(`生成 ${formatByteCount(size)}`);
  const state = boundedString(result.state);
  if (state) parts.push(`状态：${state}`);
  if (result.truncated === true) parts.push("结果已截断");
  return parts.join(" · ");
}

function joinActivityDetails(...values: string[]): string {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].join(" · ");
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && Boolean(item.trim()))
    : [];
}

function formatCommandArgument(value: string): string {
  return /\s/.test(value) ? JSON.stringify(value) : value;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function boundedNonNegativeNumber(value: unknown): number | null {
  const number = finiteNumber(value);
  return number !== null && number >= 0 ? number : null;
}

function formatByteCount(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KiB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
}

function activityStatus(value: string | null): {
  status: ExecutionActivityStatus;
  label: string;
} {
  const normalized = value?.toLowerCase() ?? "";
  if (normalized === "pending") return { status: "running", label: "等待调度" };
  if (normalized === "queued") return { status: "running", label: "正在排队" };
  if (normalized === "running") return { status: "running", label: "正在运行" };
  if (normalized === "succeeded" || normalized === "completed") {
    return { status: "succeeded", label: "执行完成" };
  }
  if (normalized === "failed") return { status: "failed", label: "执行失败" };
  if (normalized === "deadline_exceeded") return { status: "failed", label: "执行超时" };
  if (normalized === "cancelled" || normalized === "canceled") {
    return { status: "cancelled", label: "已取消" };
  }
  return { status: "unknown", label: "状态已更新" };
}

function activityDetail(
  progress: Record<string, unknown> | null,
  failure: Record<string, unknown> | null,
): string {
  const failureMessage = boundedString(failure?.message);
  if (failureMessage) return failureMessage;
  const details: string[] = [];
  const remoteStatus = boundedString(progress?.status);
  if (remoteStatus) details.push(remoteJobStatusLabel(remoteStatus));
  const queueValue = progress?.queue;
  const queue =
    queueValue && typeof queueValue === "object" && !Array.isArray(queueValue)
      ? (queueValue as Record<string, unknown>)
      : null;
  if (!queue) return details.join(" · ");
  const position = boundedInteger(queue.position);
  const jobsAhead = boundedInteger(queue.jobs_ahead);
  const runningJobs = boundedInteger(queue.running_jobs);
  const totalQueued = boundedInteger(queue.total_queued);
  if (position !== null) details.push(`当前第 ${position} 位`);
  if (jobsAhead !== null) details.push(`前方 ${jobsAhead} 个任务`);
  if (runningJobs !== null) details.push(`运行中 ${runningJobs} 个`);
  if (totalQueued !== null) details.push(`队列共 ${totalQueued} 个`);
  return details.join(" · ");
}

function remoteJobStatusLabel(value: string): string {
  const labels: Record<string, string> = {
    pending: "远端作业等待调度",
    queued: "远端作业正在排队",
    running: "远端作业正在运行",
    succeeded: "远端作业已完成",
    completed: "远端作业已完成",
    failed: "远端作业失败",
    deadline_exceeded: "远端作业超时",
    cancelled: "远端作业已取消",
    canceled: "远端作业已取消",
  };
  const normalized = value.toLowerCase();
  return labels[normalized] ?? `远端作业状态：${value}`;
}

function recordPayload(event: TaskEvent, key: string): Record<string, unknown> | null {
  const value = event.payload?.[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function boundedString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function boundedInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
}

export function ExecutionActivityRow({
  activity,
  currentTime,
  preparationBadges,
  hideTiming = false,
}: {
  hideTiming?: boolean;
  activity: ExecutionActivity;
  currentTime: number;
  preparationBadges: ReadonlyMap<string, string>;
}) {
  const duration = hideTiming ? null : executionActivityDuration(activity, currentTime);
  const preparationBadge = activity.actionIds
    .map((actionId) => preparationBadges.get(actionId))
    .find(Boolean);
  return (
    <li className={activity.status}>
      <ActivityStatusNode activity={activity} />
      <div>
        <strong>
          {activity.label}
          {preparationBadge && (
            <span className="workspace-preparation-badge">{preparationBadge}</span>
          )}
        </strong>
        <small>
          <span>
            {activity.statusLabel}
            {activity.actionIds.length > 1 && ` · ${activity.actionIds.length} 次状态检查`}
          </span>
          {duration !== null && (
            <time dateTime={`PT${Math.max(0, Math.round(duration))}S`}>
              {formatExecutionDuration(duration)}
            </time>
          )}
        </small>
        {activity.detail && (
          <p aria-live={activity.progressUpdatedAt ? "polite" : undefined}>{activity.detail}</p>
        )}
        {activity.progressUpdatedAt && (
          <small>
            <time dateTime={activity.progressUpdatedAt}>
              最后更新{" "}
              {new Date(activity.progressUpdatedAt).toLocaleTimeString("zh-CN", {
                hour12: false,
              })}
            </time>
            {activity.status === "running" &&
              currentTime - parseTimestamp(activity.progressUpdatedAt) > 60_000 && (
                <span> · 暂未收到新进度</span>
              )}
          </small>
        )}
      </div>
    </li>
  );
}

function FileRejectionRow({ notice }: { notice: FileRejectionNotice }) {
  const { issue } = notice;
  return (
    <li className="workspace-file-rejection">
      <TriangleAlert aria-hidden="true" size={13} />
      <div>
        <strong>文件输入被拒绝 · 消费者未执行</strong>
        <small>
          {notice.functionName} · {issue.pointer} [{issue.index}]
        </small>
        <p>
          {issue.actualFormat ?? "格式待确认"} → {issue.acceptedFormats.join(" / ")}
        </p>
        <small title={issue.digest}>
          {issue.fileRef.fileId} · SHA-256 {issue.digest.slice(0, 12)}…
        </small>
      </div>
    </li>
  );
}

function ActivityStatusNode({ activity }: { activity: ExecutionActivity }) {
  return (
    <span
      aria-label={`工具活动：${activity.statusLabel}`}
      className={`workspace-activity-status ${activity.status}`}
      role="img"
    >
      {activity.status === "running" && <LoaderCircle className="spin" size={10} />}
      {activity.status === "succeeded" && <Check size={9} strokeWidth={3} />}
      {activity.status === "failed" && <X size={9} strokeWidth={3} />}
      {activity.status === "cancelled" && <Minus size={9} strokeWidth={3} />}
    </span>
  );
}

function executionActivityDuration(
  activity: ExecutionActivity,
  currentTime: number,
): number | null {
  const started = parseTimestamp(activity.startedAt);
  if (activity.status === "running" && started > 0) {
    return Math.max(0, (currentTime - started) / 1000);
  }
  // 完成动作优先采用服务端授权边界派生的单动作耗时；远端作业墙钟生命周期另行展示。
  if (activity.durationSeconds !== null) return activity.durationSeconds;
  if (activity.jobId && activity.finishedAt && started > 0) {
    return Math.max(0, (parseTimestamp(activity.finishedAt) - started) / 1000);
  }
  if (activity.finishedAt && started > 0) {
    return Math.max(0, (parseTimestamp(activity.finishedAt) - started) / 1000);
  }
  return null;
}

function eventsForPlan(plan: PublicPlan, events: TaskEvent[]): TaskEvent[] {
  let startCursor = 0;
  for (const event of events) {
    if (!isPlanSnapshotEvent(event)) continue;
    const eventPlan = event.payload?.plan;
    if (!eventPlan || typeof eventPlan !== "object") continue;
    const candidate = eventPlan as Record<string, unknown>;
    if (
      candidate.plan_hash === plan.plan_hash ||
      candidate.execution_plan_id === plan.execution_plan_id
    ) {
      startCursor = Math.max(startCursor, event.cursor);
    }
  }
  if (startCursor === 0) {
    for (const event of events) {
      if (
        event.event_type === "plan_execution_started" &&
        stringPayload(event, "envelope_id") === plan.execution_plan_id
      ) {
        startCursor = Math.max(startCursor, event.cursor);
      }
    }
  }
  return events.filter((event) => event.cursor > startCursor);
}

function stringPayload(event: TaskEvent, key: string): string | null {
  const value = event.payload?.[key];
  return typeof value === "string" ? value : null;
}

function isActionStartEvent(event: TaskEvent): boolean {
  return event.event_type === "action_granted" || event.event_type === "action_started";
}

function numberPayload(event: TaskEvent, key: string): number | null {
  const value = event.payload?.[key];
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function parseTimestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatExecutionDuration(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.round(seconds));
  if (wholeSeconds < 1) return "<1秒";
  if (wholeSeconds < 60) return `${wholeSeconds}秒`;
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}分${remainingSeconds}秒` : `${minutes}分钟`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}小时${remainingMinutes}分` : `${hours}小时`;
}

function useCurrentTime(active: boolean): number {
  const [currentTime, setCurrentTime] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setCurrentTime(Date.now());
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return currentTime;
}

function planPlaceholderTitle(state: TaskRuntimeState["state"]): string {
  if (state === "planning") return "正在生成计划";
  if (state === "executing" || state === "running") return "即时执行";
  if (state === "failed") return "计划未生成";
  return "等待任务计划";
}

function planPlaceholderDetail(state: TaskRuntimeState["state"]): string {
  if (state === "planning") return "计划生成后将在这里显示执行进度。";
  if (state === "executing" || state === "running") {
    return "正在等待首个动作；规划创建后会在这里按步骤组织活动。";
  }
  if (state === "failed") return "本次任务未生成可执行计划。";
  return "提交任务目标后，当前计划会显示在这里。";
}
